import React, { ReactElement } from "react";
import styled from "styled-components";

import { trackEvent } from "@trailhead/analytics";
import {
  breakpoints,
  Button as AsurionButton,
  ButtonVariant,
  Text,
  Size,
} from "@trailhead/asurion-ui";
import {
  ExpertSupportButtonType,
  hasMissingEmbeddedContent,
} from "@trailhead/contentful";
import { FlexColumnContainer, useWindowDimensions } from "@trailhead/core-ui";
import { useSanitizedTranslation } from "@trailhead/i18n";

import { useChatClient } from "./useChatClient";
import { useIsChatEnabled } from "./useIsChatEnabled";
import { ExpertSupportProps } from "./propTypes";

const QuestionsContainer = styled.div`
  width: 100%;
  max-width: 600px;
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  background-color: #f0f0f5;
  align-items: center;
  position: relative;

  ${breakpoints.xxl} {
    min-width: 600px;
    flex-direction: row;
  }
`;

const MarginContainer = styled.div`
  margin-bottom: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const CallToActionContainer = styled(FlexColumnContainer)`
  gap: 24px;
  padding: 32px 16px 16px;

  ${breakpoints.xxl} {
    align-selft: start;
    padding: 24px;
  }
`;

const CallToActionHeadersContainer = styled(FlexColumnContainer)`
  gap: 8px;
`;

const CallToActionHeader = styled(Text)`
  display: block;
  text-align: center;
  line-height: 130%;

  ${breakpoints.xxl} {
    padding-top: 5px;
    text-align: left;
  }
`;

const CallToActionSubHeader = styled(Text)`
  display: block;
  text-align: center;
  line-height: 130%;

  ${breakpoints.xxl} {
    text-align: left;
  }
`;

const ImageContainer = styled.div`
  width: 100%;
  max-width: 350px;
  margin: 0 auto;
  display: block;

  ${breakpoints.xxl} {
    max-width: 300px;
    margin-top: 20px;
    margin-right: 5px;
  }
`;

const AsurionButtonWrapper = styled(AsurionButton)`
  align-self: center;
  line-height: none;
  max-height: 32px;
  max-width: 260px;

  ${breakpoints.xxl} {
    max-width: 130px;
    margin-right: 20px;
  }
`;

const CallToActionButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  ${breakpoints.xxl} {
    flex-direction: row;
  }
`;

const DEFAULT_SUBTEXT = "We're here for you if you need us.";

export const ExpertSupportCTA: React.FC<ExpertSupportProps> = ({
  content = {
    title: "Talk to an expert",
    subtext: DEFAULT_SUBTEXT,
    primaryButtonText: "Call an expert",
    primaryButtonType: ExpertSupportButtonType.CALL,
    secondaryButtonText: "Chat with us",
    secondaryButtonType: ExpertSupportButtonType.CHAT,
    image: undefined,
    supportNumber: undefined,
  },
}) => {
  const t = useSanitizedTranslation();
  const ChatClient = useChatClient();
  const openChat = (): void => {
    trackEvent("ExpertSupportCTA_Chat_Click");
    ChatClient.openMessagingOverlay();
  };
  const enableMessaging = useIsChatEnabled();

  const openCall = (): void => {
    if (content.supportNumber) {
      window.open(`tel: ${content.supportNumber}`, "_self");
      trackEvent("ExpertSupportCTA_Call_Click", {
        buttonText: content.primaryButtonText,
      });
    }
  };

  const renderSubtext = (): string => {
    const subtext = content.subtext ? content.subtext : "";
    return hasMissingEmbeddedContent(subtext) ? DEFAULT_SUBTEXT : subtext;
  };

  const { width } = useWindowDimensions();
  const isDesktopWide = width >= Size.XXL;

  const renderButtonWithType = (
    buttonText: string,
    buttonType: ExpertSupportButtonType,
    buttonVariant: ButtonVariant,
  ): ReactElement | null => {
    switch (buttonType) {
      case ExpertSupportButtonType.CALL:
        return content.supportNumber ? (
          <AsurionButtonWrapper
            {...(isDesktopWide ? {} : { iconSrc: "Phone" })}
            size="medium"
            variant={buttonVariant}
            onClick={openCall}
          >
            <div
              dangerouslySetInnerHTML={{
                __html: t(buttonText),
              }}
            />
          </AsurionButtonWrapper>
        ) : null;
      case ExpertSupportButtonType.CHAT:
        return enableMessaging ? (
          <AsurionButtonWrapper
            {...(isDesktopWide ? {} : { iconSrc: "ChatBubbleCircle" })}
            size="medium"
            variant={buttonVariant}
            onClick={openChat}
          >
            <div
              dangerouslySetInnerHTML={{
                __html: t(buttonText),
              }}
            />
          </AsurionButtonWrapper>
        ) : null;
      default:
        return null;
    }
  };

  return (
    <MarginContainer>
      <QuestionsContainer>
        <CallToActionContainer>
          <CallToActionHeadersContainer>
            <CallToActionHeader
              size={4}
              weight="feather"
              dangerouslySetInnerHTML={{
                __html: t(content.title),
              }}
            />
            <CallToActionSubHeader
              dangerouslySetInnerHTML={{
                __html: t(renderSubtext()),
              }}
            />
          </CallToActionHeadersContainer>
          <CallToActionButtonsContainer>
            {content.primaryButtonText && content.primaryButtonType
              ? renderButtonWithType(
                  content.primaryButtonText,
                  content.primaryButtonType,
                  "default",
                )
              : null}
            {content.secondaryButtonText && content.secondaryButtonType
              ? renderButtonWithType(
                  content.secondaryButtonText,
                  content.secondaryButtonType,
                  isDesktopWide ? "text" : "flat",
                )
              : null}
          </CallToActionButtonsContainer>
        </CallToActionContainer>
        {content.image ? (
          <ImageContainer>{content.image}</ImageContainer>
        ) : null}
      </QuestionsContainer>
    </MarginContainer>
  );
};
